import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../data/cloud-data.js';
import { Configuration } from '../../data/configuration.js';
import { ExteriorConfigurator } from '../../configurator/exterior-configurator.js';
import { VehicleView } from '../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../view-content.js';
import SelectedSectionItem from '../ui/selected-section-item.tsx';
import ViewContentRows from '../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface GraphicsViewProps {
	exteriorConfigurator: ExteriorConfigurator;
	onBackButtonClicked: () => void;
}

interface GraphicsViewContextType {
	selectedDoorGraphicItemId?: string;
	handleDoorGraphicItemClicked?: (id: string) => void;
	selectedStripeGraphicItemId?: string;
	handleStripeGraphicItemClicked?: (id: string) => void;
	selectedSillGraphicItemId?: string;
	handleSillGraphicItemClicked?: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function GraphicsView(props: GraphicsViewProps) {
	if (Configuration.isSupersport400Model()) {
		return GraphicsView400(props);
	}
	else {
		return GraphicsViewStandard(props);
	}
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

function GraphicsView400(props: GraphicsViewProps) {

	// initial values.
	const initialStripeGraphicItemId = Configuration.stripeGraphicOption;
	const initialStripeGraphicItemName = () => getItemName(initialStripeGraphicItemId);
	const initialSillGraphicItemId = Configuration.sillGraphicOption;
	const initialSillGraphicItemName = () => getItemName(initialSillGraphicItemId);

	// states.
	const [selectedStripeGraphicItemId, setSelectedStripeGraphicItemId] = useState(initialStripeGraphicItemId);
	const [selectedStripeGraphicItemName, setSelectedStripeGraphicItemName] = useState(initialStripeGraphicItemName);
	const [selectedSillGraphicItemId, setSelectedSillGraphicItemId] = useState(initialSillGraphicItemId);
	const [selectedSillGraphicItemName, setSelectedSillGraphicItemName] = useState(initialSillGraphicItemName);

	// handlers.

	const handleStripeGraphicItemClicked = (id: string) => {

		// toggle off if already selected.
		if (Configuration.stripeGraphicOption === id) {
			id = 'NONE';
		}

		// update the selected item and item name.
		setSelectedStripeGraphicItemId(id);
		setSelectedStripeGraphicItemName(getItemName(id));

		// update the configuration.
		Configuration.stripeGraphicOption = id;
		Configuration.updateCosts();

		// select the stripe graphic option on the car.
		props.exteriorConfigurator.selectStripeGraphicOption(id);
	};

	const handleSillGraphicItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedSillGraphicItemId(id);
		setSelectedSillGraphicItemName(getItemName(id));

		// update the configuration.
		Configuration.sillGraphicOption = id;
		Configuration.updateCosts();

		// select the sill graphic option on the car.
		props.exteriorConfigurator.selectSillGraphicOption(id);
	};

	// return the HTML.
	return (
		<GraphicsViewContext.Provider value={{ selectedStripeGraphicItemId, handleStripeGraphicItemClicked, selectedSillGraphicItemId, handleSillGraphicItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-graphics">
							GRAPHICS
						</h2>
					</div>

					<SectionPanel
						subHeading={'STRIPE'}
						modalContent={CloudData.getModalContent(selectedStripeGraphicItemId, CloudData.graphicsOptions)}
					>
						<SelectedSectionItem
							itemName={selectedStripeGraphicItemName}
							sectionPrice={CloudData.getPrice(selectedStripeGraphicItemId, CloudData.graphicsOptions, { ref: false })}
						/>
						<StripeGraphicItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'SILL'}
						modalContent={CloudData.getModalContent(selectedSillGraphicItemId, CloudData.graphicsOptions)}
					>
						<SelectedSectionItem
							itemName={selectedSillGraphicItemName}
							sectionPrice={CloudData.getPrice(selectedSillGraphicItemId, CloudData.graphicsOptions, { ref: false })}
						/>
						<SillGraphicItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</GraphicsViewContext.Provider>
	);
}

function GraphicsViewStandard(props: GraphicsViewProps) {

	// initial values.
	const initialDoorGraphicItemId = Configuration.doorGraphicOption;
	const initialDoorGraphicItemName = () => getItemName(initialDoorGraphicItemId);

	// states.
	const [selectedDoorGraphicItemId, setSelectedDoorGraphicItemId] = useState(initialDoorGraphicItemId);
	const [selectedDoorGraphicItemName, setSelectedDoorGraphicItemName] = useState(initialDoorGraphicItemName);

	// handlers.

	const handleDoorGraphicItemClicked = (id: string) => {

		// toggle off if already selected.
		if (Configuration.doorGraphicOption === id) {
			id = 'NONE';
		}

		// update the selected item and item name.
		setSelectedDoorGraphicItemId(id);
		setSelectedDoorGraphicItemName(getItemName(id));

		// update the configuration.
		Configuration.doorGraphicOption = id;
		Configuration.updateCosts();

		// select the door graphic option on the car.
		props.exteriorConfigurator.selectDoorGraphicOption(id);
	};

	// return the HTML.
	return (
		<GraphicsViewContext.Provider value={{ selectedDoorGraphicItemId, handleDoorGraphicItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-graphics">
							GRAPHICS
						</h2>
					</div>

					<SectionPanel
						subHeading={'DOOR ROUNDEL DESIGN'}
						modalContent={CloudData.getModalContent(selectedDoorGraphicItemId, CloudData.graphicsOptions)}
					>
						<SelectedSectionItem
							itemName={selectedDoorGraphicItemName}
							sectionPrice={CloudData.getPrice(selectedDoorGraphicItemId, CloudData.graphicsOptions, { ref: false })}
						/>
						<DoorGraphicItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</GraphicsViewContext.Provider>
	);
}

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const GraphicsViewContext = createContext<GraphicsViewContextType | undefined>(undefined);

function DoorGraphicItems() {
	return (
		<ViewContentRows maxRowLength={11}>
			<DoorGraphicItem id={'DK003'} imageFilePath={'matte'} />
			<DoorGraphicItem id={'DK060'} imageFilePath={'0'} />
			<DoorGraphicItem id={'DK061'} imageFilePath={'1'} />
			<DoorGraphicItem id={'DK062'} imageFilePath={'2'} />
			<DoorGraphicItem id={'DK063'} imageFilePath={'3'} />
			<DoorGraphicItem id={'DK064'} imageFilePath={'4'} />
			<DoorGraphicItem id={'DK065'} imageFilePath={'5'} />
			<DoorGraphicItem id={'DK066'} imageFilePath={'6'} />
			<DoorGraphicItem id={'DK067'} imageFilePath={'7'} />
			<DoorGraphicItem id={'DK068'} imageFilePath={'8'} />
			<DoorGraphicItem id={'DK069'} imageFilePath={'9'} />
		</ViewContentRows>
	);
}

function DoorGraphicItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(GraphicsViewContext);
	if (!context) {
		throw new Error('GraphicsViewContext must be used within a GraphicsView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/graphics/door-graphic/${props.imageFilePath}.svg`}
			isSelected={props.id === context.selectedDoorGraphicItemId}
			onItemClicked={context.handleDoorGraphicItemClicked!}
		/>
	);
}

function StripeGraphicItems() {
	return (
		<ViewContentRows>
			<StripeGraphicItem id={'GDS003'} imageFilePath={'light'} />
			<StripeGraphicItem id={'GDS004'} imageFilePath={'dark'} />
		</ViewContentRows>
	);
}

function StripeGraphicItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(GraphicsViewContext);
	if (!context) {
		throw new Error('GraphicsViewContext must be used within a GraphicsView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/graphics/stripe-graphic/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedStripeGraphicItemId}
			onItemClicked={context.handleStripeGraphicItemClicked!}
		/>
	);
}

function SillGraphicItems() {
	return (
		<ViewContentRows>
			<SillGraphicItem id={'GDS001'} imageFilePath={'silver'} />
			<SillGraphicItem id={'GDS002'} imageFilePath={'coral'} />
		</ViewContentRows>
	);
}

function SillGraphicItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(GraphicsViewContext);
	if (!context) {
		throw new Error('GraphicsViewContext must be used within a GraphicsView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/graphics/sill-graphic/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedSillGraphicItemId}
			onItemClicked={context.handleSillGraphicItemClicked!}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.graphicsOptions);
}
